import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { capitalizeFirstLetter, showAlert } from "utils/utils";
import {
  deleteFolder,
  deleteMediaLibrary,
  deleteMultipleMedia,
  moveMediaToFolder,
} from "api";

export const MediaLibraryContext = createContext(null);

const MediaLibraryContextProvider = ({ children }) => {
  const queryClient = useQueryClient();

  const [parentFolder, setParentFolder] = useState(null);
  const [mediaDetail, setMediaDetail] = useState(null);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showUpdateFolderModal, setShowUpdateFolderModal] = useState(false);
  const [showUpdateFileModal, setShowUpdateFileModal] = useState(false);
  const [showMediaDetailModal, setShowMediaDetailModal] = useState(false);
  const [showDeleteSingleItemPrompt, setShowDeleteSingleItemPrompt] =
  useState(false);
  const [importFromUrl, setImportFromUrl] = useState(false);
  const [targetItem, setTargetItem] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [showDeleteMultipleItemsPrompt, setShowDeleteMultipleItemsPrompt] = useState(false);

  const { pathname } = useLocation();

  const closeMediaLib = () => {
    if (!showMediaLibrary) return;
    setShowMediaLibrary(false);
    setTargetItem(null);
    setParentFolder(null)
  };

  const handleDeleteItem = useMutation({
    mutationFn: () => {
      const deleteFn =
        targetItem?.type === "folder" ? deleteFolder : deleteMediaLibrary;

      return deleteFn(targetItem.id);
    },
    onError: (error, variables, context) => {
      showAlert({
        msg: "Failed to delete item!",
        type: "danger",
      });
    },
    onSuccess: (data, variables, context) => {
      if (targetItem.type === "folder") {
        queryClient.invalidateQueries({ queryKey: ["allMediaFolders"] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["allMedia"] });
      }
      showAlert({
        msg: `${capitalizeFirstLetter(targetItem.type)} deleted!`,
        type: "success",
      });
    },
    onSettled: () => {
      setShowDeleteSingleItemPrompt(false);
      setTargetItem(null);
    },
  });

  const handleDeleteMultipleItems = useMutation({
    mutationFn: () => {
      const data = {
        media_ids: selectedFiles?.map((item)=> item.id),
        folder_ids: selectedFolders?.map((item)=> item.id)
      }
      if(!selectedFolders.length) delete data.folder_ids;
      if(!selectedFiles.length) delete data.media_ids;

      return deleteMultipleMedia(data);
    },
    onError: (error, variables, context) => {
      // An error happened!
      showAlert({
        msg: "Failed to delete items!",
        type: "danger",
      });
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["allMediaFolders"] });
      queryClient.invalidateQueries({ queryKey: ["allMedia"] });
      showAlert({
        msg: `Items deleted!`,
        type: "success",
      });
    },
    onSettled: () => {
      setShowDeleteMultipleItemsPrompt(false);
      setSelectedFolders([]);
      setSelectedFiles([]);
    },
  });

  const handleMoveFilesToFolder = useMutation({
    mutationFn: ({ mediaIds, folderId }) =>
      moveMediaToFolder({
        media_ids: mediaIds,
        folder_id: folderId || null,
      }),
    onError: () => {
      showAlert({
        msg: "Failed to move files!",
        type: "danger",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["allMediaFolders"] });
      queryClient.invalidateQueries({ queryKey: ["allMedia"] });
      showAlert({
        msg: "Files moved!",
        type: "success",
      });
    },
    onSettled: () => {
      setSelectedFiles([]);
    },
  });

  useEffect(() => {
    closeMediaLib();
    // eslint-disable-next-line
  }, [pathname]);

  return (
    <MediaLibraryContext.Provider
      value={{
        setShowMediaLibrary,
        showMediaLibrary,
        showCreateFolderModal,
        setShowCreateFolderModal,
        showUpdateFolderModal,
        setShowUpdateFolderModal,
        showUpdateFileModal,
        setShowUpdateFileModal,
        showDeleteSingleItemPrompt,
        setShowDeleteSingleItemPrompt,
        targetItem,
        setTargetItem,
        handleDeleteItem,
        parentFolder,
        setParentFolder,
        showMediaDetailModal,
        setShowMediaDetailModal,
        mediaDetail,
        setMediaDetail,
        importFromUrl,
        setImportFromUrl,
        selectedFiles,
        setSelectedFiles,
        selectedFolders,
        setSelectedFolders,
        showDeleteMultipleItemsPrompt,
        setShowDeleteMultipleItemsPrompt,
        handleDeleteMultipleItems,
        handleMoveFilesToFolder,
      }}
    >
      {children}
    </MediaLibraryContext.Provider>
  );
};

export default MediaLibraryContextProvider;
